/**
 * Sirv Smart Viewer integration (SFCC js/sirv.js + variant.js parity).
 * Starts Sirv on .Sirv containers and re-inits after variant gallery morphs.
 */

import {
  buildSirvSizedUrl,
  clearSirvFolderCache,
  getSirvFolderGalleryUrls,
  hydrateSirvFolderTargets,
  isFolderStaticGridGallery,
  observeSirvFolderPlpTiles,
  populateSirvGridPlaceholder,
} from './sirv-folder-manifest.js';

const SIRV_SCRIPT_SELECTOR = 'script[data-sirv-script]';
const SIRV_SCRIPT_BASE =
  'https://scripts.sirv.com/sirvjs/v3/sirv.js?v=3.15.1&modules=';

/**
 * @returns {boolean}
 */
function pageNeedsFullSirvModules() {
  return Boolean(document.querySelector('[data-sirv-pdp-gallery]'));
}

/**
 * Injects Sirv when AJAX sections add .Sirv tiles on pages without sirv-head.
 *
 * @returns {Promise<void>}
 */
function ensureSirvScriptTag() {
  const existing = document.querySelector(SIRV_SCRIPT_SELECTOR);
  if (existing) {
    return loadSirvScript();
  }

  if (!document.querySelector('.Sirv')) {
    return Promise.reject(new Error('No Sirv containers on page'));
  }

  window.SirvEnabled = true;
  const modules = pageNeedsFullSirvModules()
    ? 'lazyimage,zoom,video,gallery'
    : 'lazyimage';
  const script = document.createElement('script');
  script.src = `${SIRV_SCRIPT_BASE}${modules}`;
  script.defer = true;
  script.dataset.sirvScript = '';
  document.head.appendChild(script);
  return loadSirvScript();
}

/**
 * @returns {Promise<void>}
 */
function loadSirvScript() {
  if (typeof window.Sirv !== 'undefined') {
    return Promise.resolve();
  }

  const existing = document.querySelector(SIRV_SCRIPT_SELECTOR);
  if (!existing) {
    return ensureSirvScriptTag();
  }

  if (existing.dataset.sirvLoaded === 'true') {
    return waitForSirvGlobal();
  }

  return new Promise((resolve, reject) => {
    existing.addEventListener(
      'load',
      () => {
        existing.dataset.sirvLoaded = 'true';
        waitForSirvGlobal().then(resolve).catch(reject);
      },
      { once: true }
    );
    existing.addEventListener(
      'error',
      () => reject(new Error('Sirv script failed to load')),
      { once: true }
    );
    if (existing.complete || existing.readyState === 'complete') {
      existing.dataset.sirvLoaded = 'true';
      waitForSirvGlobal().then(resolve).catch(reject);
    }
  });
}

/**
 * @returns {Promise<void>}
 */
function waitForSirvGlobal() {
  if (typeof window.Sirv !== 'undefined') {
    return Promise.resolve();
  }

  return new Promise((resolve) => {
    const deadline = Date.now() + 10000;
    const tick = () => {
      if (typeof window.Sirv !== 'undefined') {
        resolve();
        return;
      }
      if (Date.now() > deadline) {
        resolve();
        return;
      }
      requestAnimationFrame(tick);
    };
    tick();
  });
}

/**
 * @param {Element} viewer
 * @returns {boolean}
 */
function isSirvGridGalleryReady(viewer) {
  const smv = viewer.querySelector('.smv');
  if (!smv) {
    return false;
  }

  // Any SMV mount is enough to hand off from the LCP placeholder.
  // Zoom needs the live .Sirv viewer; do not wait for layout-specific chrome.
  return (
    smv.children.length > 0 ||
    smv.querySelector('.smv-slide, .smv-item, [data-type="zoom"], img') != null
  );
}

/**
 * @param {Element} viewer
 * @param {boolean} pending
 */
function setGridViewerPending(viewer, pending) {
  if (pending) {
    viewer.setAttribute('data-sirv-grid-pending', 'true');
    return;
  }
  viewer.removeAttribute('data-sirv-grid-pending');
}

/**
 * @param {Element} gallery
 */
function revealSirvGridGallery(gallery) {
  if (isFolderStaticGridGallery(gallery)) {
    return;
  }

  gallery.classList.add('sirv-pdp-gallery--ready');
  gallery.querySelector('.sirv-pdp-gallery__grid-placeholder')?.remove();
  const viewer = gallery.querySelector('.sirv-pdp-gallery__viewer');
  if (viewer) {
    setGridViewerPending(viewer, false);
  }
}

/**
 * @param {Element} gallery
 * @param {Element} viewer
 */
function expandGridStallFallback(gallery, viewer) {
  const folderUrls = getSirvFolderGalleryUrls(gallery);
  if (folderUrls.length) {
    populateSirvGridPlaceholder(gallery, folderUrls);
    return;
  }

  const placeholder = gallery.querySelector('.sirv-pdp-gallery__grid-placeholder');
  if (!placeholder || !viewer) {
    return;
  }

  const sources = [];
  const hero = viewer.querySelector('.sirv-pdp-gallery__hero');
  if (hero?.dataset.src) {
    sources.push(hero.dataset.src);
  } else if (hero?.src) {
    sources.push(hero.src);
  }

  viewer.querySelectorAll('[data-src]').forEach((tile) => {
    const src = tile.dataset.src;
    if (src && !sources.includes(src)) {
      sources.push(src);
    }
  });

  if (!sources.length) {
    return;
  }

  const gridPlaceholderWidth = parseInt(gallery.dataset.sirvGridPlaceholderWidth || '403', 10);
  const quality = parseInt(gallery.dataset.sirvQuality || '60', 10);
  placeholder.innerHTML = '';

  sources.forEach((src, index) => {
    const img = document.createElement('img');
    img.className = 'sirv-pdp-gallery__grid-placeholder-item';
    img.src = buildSirvSizedUrl(src.split('?')[0], {
      width: gridPlaceholderWidth,
      height: gridPlaceholderWidth,
      quality,
    });
    img.alt = '';
    img.width = gridPlaceholderWidth;
    img.height = gridPlaceholderWidth;
    img.loading = index === 0 ? 'eager' : 'lazy';
    if (index === 0) {
      img.fetchPriority = 'high';
    }
    placeholder.appendChild(img);
  });
}

/**
 * @param {Element} viewer
 */
function restartSirvViewer(viewer) {
  if (typeof window.Sirv === 'undefined') {
    return;
  }

  if (typeof window.Sirv.stop === 'function') {
    try {
      window.Sirv.stop(viewer);
    } catch (error) {
      /* Viewer may not have been started yet */
    }
  }

  viewer.removeAttribute('data-sirv-started');

  try {
    window.Sirv.start(viewer);
    viewer.setAttribute('data-sirv-started', 'true');
  } catch (error) {
    console.warn('[sirv-product-images] Sirv restart failed', error);
  }
}

/**
 * @param {Element} viewer
 */
function markGridGalleryReady(viewer) {
  const gallery = viewer.closest('[data-sirv-layout="grid"]');
  if (!gallery || gallery.classList.contains('sirv-pdp-gallery--ready')) {
    return;
  }

  if (isFolderStaticGridGallery(gallery)) {
    return;
  }

  const finish = () => {
    requestAnimationFrame(() => {
      revealSirvGridGallery(gallery);
    });
  };

  const stall = () => {
    console.warn('[sirv-product-images] Sirv grid did not initialize; showing static grid fallback');
    expandGridStallFallback(gallery, viewer);
    viewer.classList.remove('Sirv');
    viewer.removeAttribute('data-sirv-started');
    gallery.classList.add('sirv-pdp-gallery--grid-stalled');
    setGridViewerPending(viewer, false);
  };

  if (isSirvGridGalleryReady(viewer)) {
    finish();
    return;
  }

  let attempts = 0;
  let finished = false;
  const observer =
    typeof MutationObserver !== 'undefined'
      ? new MutationObserver(() => {
          if (finished || !isSirvGridGalleryReady(viewer)) {
            return;
          }
          finished = true;
          observer.disconnect();
          finish();
        })
      : null;

  observer?.observe(viewer, { childList: true, subtree: true });

  const tick = () => {
    if (finished) {
      return;
    }
    if (isSirvGridGalleryReady(viewer)) {
      finished = true;
      observer?.disconnect();
      finish();
      return;
    }
    if (attempts === 90) {
      restartSirvViewer(viewer);
    }
    if (attempts >= 240) {
      finished = true;
      observer?.disconnect();
      stall();
      return;
    }
    attempts += 1;
    requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);
}

/**
 * @param {Element} root
 */
function startSirvIn(root) {
  if (!window.SirvEnabled || typeof window.Sirv === 'undefined' || !root) {
    return;
  }

  root
    .querySelectorAll('.Sirv:not([data-sirv-started]):not(.image-hover), .sirv-pdp-gallery__viewer:not([data-sirv-started])')
    .forEach((viewer) => {
      const gallery = viewer.closest('[data-sirv-pdp-gallery]');
      if (gallery && isFolderStaticGridGallery(gallery)) {
        return;
      }

      if (viewer.matches('.sirv-pdp-gallery__viewer') && viewer.hasAttribute('data-sirv-await-hydrate')) {
        return;
      }

      if (viewer.matches('.sirv-pdp-gallery__viewer') && !viewer.children.length) {
        return;
      }

      try {
        window.Sirv.start(viewer);
        viewer.setAttribute('data-sirv-started', 'true');
        if (viewer.closest('[data-sirv-layout="grid"]')) {
          markGridGalleryReady(viewer);
        }
      } catch (error) {
        console.warn('[sirv-product-images] Sirv.start failed', error);
        const gallery = viewer.closest('[data-sirv-layout="grid"]');
        if (gallery) {
          expandGridStallFallback(gallery, viewer);
          viewer.classList.remove('Sirv');
          viewer.removeAttribute('data-sirv-started');
          gallery.classList.add('sirv-pdp-gallery--grid-stalled');
          setGridViewerPending(viewer, false);
        }
      }
    });
}

/**
 * @param {CustomEvent} event
 */
function handleVariantUpdate(event) {
  if (!event.detail?.data?.html) {
    return;
  }

  // media-gallery morphs first on every variant option (including Size). When it marks
  // data-gallery-media-unchanged we skip Sirv remount; that path is unused for normal
  // swatch/size updates (full morph + remount for both).
  const liveGallery = document.querySelector('media-gallery');
  if (liveGallery instanceof HTMLElement && liveGallery.dataset.galleryMediaUnchanged === 'true') {
    delete liveGallery.dataset.galleryMediaUnchanged;
    return;
  }

  clearSirvFolderCache();

  requestAnimationFrame(() => {
    hydrateSirvFolderTargets(document)
      .then(() => loadSirvScript())
      .then(() => {
        document.querySelectorAll('[data-sirv-pdp-gallery]').forEach((gallery) => {
          if (isFolderStaticGridGallery(gallery)) {
            return;
          }

          gallery.classList.remove('sirv-pdp-gallery--ready', 'sirv-pdp-gallery--grid-stalled');
          gallery.querySelectorAll('.Sirv').forEach((viewer) => {
            viewer.removeAttribute('data-sirv-started');
            if (gallery.matches('[data-sirv-layout="grid"]')) {
              setGridViewerPending(viewer, true);
            }
          });
          startSirvIn(gallery);
        });
        startSirvIn(document);
        observeSirvFolderPlpTiles();
      });
  });
}

function init() {
  const hasSirvMarkup = document.querySelector(
    '.Sirv, [data-sirv-pdp-gallery], [data-sirv-fetch-folder]'
  );
  if (!window.SirvEnabled && !hasSirvMarkup) {
    return;
  }

  const bootstrap = async () => {
    await hydrateSirvFolderTargets(document);
    await loadSirvScript();
    startSirvIn(document);
    if (document.querySelector('[data-sirv-plp-tile][data-sirv-fetch-folder]')) {
      observeSirvFolderPlpTiles();
    }
  };

  bootstrap().catch(() => {});

  document.addEventListener('variant:update', handleVariantUpdate);
  document.addEventListener('sirv:folder-hydrated', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) {
      return;
    }

    if (target.matches('[data-sirv-pdp-gallery]') && isFolderStaticGridGallery(target)) {
      return;
    }

    if (target.matches('[data-sirv-plp-tile]')) {
      return;
    }

    loadSirvScript()
      .then(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            startSirvIn(target);
          });
        });
      })
      .catch(() => {});
  });
  document.addEventListener('shopify:section:load', () => {
    hydrateSirvFolderTargets(document)
      .then(() => loadSirvScript())
      .then(() => {
        startSirvIn(document);
        if (document.querySelector('[data-sirv-plp-tile][data-sirv-fetch-folder]')) {
          observeSirvFolderPlpTiles();
        }
      })
      .catch(() => {});
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}

export {};
